// /app/day/:day — every question of one IST day (owner's request 2026-10-30). Opened by
// clicking a day on Home's "Questions solved — last 30 days".
//
// List: one row per question (oldest first) — colour strip + label (Correct green, Wrong red,
// Not answered grey), "Q <id>", where it came from and when, and the first 3 lines of the
// question. Filter buttons All / Correct / Wrong / Not answered (kept in ?show= so Back
// returns to the same view). Previous / Next day.
// Clicking a row opens the full question in a pop-up (same look as the test result card:
// options with his answer and the right answer, explanation, notes references), with
// Previous / Next question inside the current filter; Esc or the × closes it.
// Numbers from GET /api/v2/dashboard/day/<date> (services/v2_day.py).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getDayQuestions } from '../../api/v2';
import { useActivityArea } from '../../components/app/activity';
import { IconArrowLeft, IconArrowRight, IconClose } from '../../components/app/icons';
import {
  AnswerPanel, LETTERS, OptionRow, POINT_CSS, PyqBadge, QId, QuestionBody, card, revealedState,
} from '../../components/app/testParts';
import { C, FONT, button } from '../../components/app/theme';
import { LoadError, Loading } from '../../components/app/LoadState';

const GREY = '#64748B';
const STATUS = {
  correct: { color: '#059669', bg: C.goodBg, text: C.good, label: 'Correct' },
  incorrect: { color: C.bad, bg: '#FEE2E2', text: C.bad, label: 'Wrong' },
  unattempted: { color: '#94A3B8', bg: '#F1F5F9', text: GREY, label: 'Not answered' },
};
const FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'correct', label: 'Correct' },
  { key: 'incorrect', label: 'Wrong' },
  { key: 'unattempted', label: 'Not answered' },
];
const URL_OF = { all: null, correct: 'correct', incorrect: 'wrong', unattempted: 'skipped' };
const FROM_URL = { correct: 'correct', wrong: 'incorrect', skipped: 'unattempted' };

const MONTHS_LONG = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August',
  'September', 'October', 'November', 'December'];
const DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

function parseDay(iso) {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso || '');
  if (!m) return null;
  const dt = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3]));
  return Number.isNaN(dt.getTime()) ? null : dt;
}
const isoOf = (dt) => dt.toISOString().slice(0, 10);
function shiftDay(iso, by) {
  const dt = parseDay(iso);
  dt.setUTCDate(dt.getUTCDate() + by);
  return isoOf(dt);
}
function istTodayIso() {
  return isoOf(new Date(Date.now() + 330 * 60000));
}
function longDay(iso) {
  const dt = parseDay(iso);
  return `${DAYS_LONG[dt.getUTCDay()]}, ${dt.getUTCDate()} ${MONTHS_LONG[dt.getUTCMonth()]} ${dt.getUTCFullYear()}`;
}
function timeOf(at) {
  if (!at) return '';
  try {
    return new Date(at).toLocaleTimeString('en-IN', { timeZone: 'Asia/Kolkata', hour: 'numeric', minute: '2-digit' });
  } catch { return ''; }
}
const notesRefHref = (ref) => (ref && ref.chapter_id
  ? `/app/notes/${ref.chapter_id}?at=${encodeURIComponent(ref.point_code || ref.code)}` : null);

// ------------------------------------------------------------------ pieces

function StatusPill({ status }) {
  const st = STATUS[status] || STATUS.unattempted;
  return (
    <span data-testid="v2-day-status" style={{
      background: st.bg, color: st.text, padding: '3px 10px', borderRadius: 999,
      fontWeight: 700, fontSize: 12, whiteSpace: 'nowrap',
    }}>{st.label}</span>
  );
}

function answerWords(q) {
  if (q.status === 'unattempted') return `Right answer ${q.correct_option || '–'}`;
  if (q.status === 'correct') return `Your answer ${q.your_answer}`;
  return `Your answer ${q.your_answer} · Right answer ${q.correct_option || '–'}`;
}

function Row({ q, onOpen }) {
  const st = STATUS[q.status] || STATUS.unattempted;
  return (
    <button type="button" data-testid="v2-day-row" data-status={q.status} onClick={onOpen} className="v2-dayrow" style={{
      all: 'unset', boxSizing: 'border-box', cursor: 'pointer', display: 'flex', alignItems: 'stretch',
      background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: 'hidden',
      fontFamily: FONT.ui, color: C.text, width: '100%',
    }}>
      <div style={{ flex: '0 0 6px', background: st.color }} />
      <div style={{ flex: '1 1 auto', minWidth: 0, padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, fontSize: 13, color: C.muted }}>
          <span style={{ fontWeight: 600, color: C.text }}>{q.n}.</span>
          <span data-testid="v2-qid" style={{ fontWeight: 600, color: C.text, whiteSpace: 'nowrap' }}>Q {q.question_id}</span>
          <StatusPill status={q.status} />
          <span style={{ whiteSpace: 'nowrap' }}>{q.source}{q.at ? ` · ${timeOf(q.at)}` : ''}</span>
          <span style={{ marginLeft: 'auto', whiteSpace: 'nowrap', fontWeight: 500, color: st.text }}>{answerWords(q)}</span>
        </div>
        <div data-testid="v2-day-preview" style={{
          fontSize: 14, lineHeight: 1.5, color: C.text, overflow: 'hidden',
          display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical', wordBreak: 'break-word',
        }}>{q.preview || '(No question text — open to see it)'}</div>
      </div>
    </button>
  );
}

function QuestionPopup({ q, index, count, onClose, onPrev, onNext }) {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && onPrev) onPrev();
      else if (e.key === 'ArrowRight' && onNext) onNext();
    };
    window.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = prevOverflow; };
  }, [onClose, onPrev, onNext]);

  const st = STATUS[q.status] || STATUS.unattempted;
  return (
    <div role="dialog" aria-modal="true" aria-label={`Question ${q.question_id}`} data-testid="v2-day-popup"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 60, background: 'rgba(15,23,42,0.5)',
        display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '24px 16px', overflowY: 'auto',
      }}>
      <div style={{
        ...card, width: '100%', maxWidth: 820, padding: 0, border: `2px solid ${st.color}`,
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          position: 'sticky', top: -24, zIndex: 1, background: C.surface, borderRadius: '12px 12px 0 0',
          borderBottom: `1px solid ${C.border}`, padding: '12px 16px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10,
        }}>
          <StatusPill status={q.status} />
          <QId id={q.question_id} style={{ fontSize: 14, color: C.text, fontWeight: 600 }} />
          <span style={{ fontSize: 13, color: C.muted }}>{q.source}{q.at ? ` · ${timeOf(q.at)}` : ''}</span>
          <span style={{ marginLeft: 'auto', fontSize: 13, color: C.muted }}>{index + 1} of {count}</span>
          <button type="button" onClick={onClose} aria-label="Close" data-testid="v2-day-popup-close" style={{
            ...button.secondary, minHeight: 40, padding: '0 10px', display: 'inline-flex', alignItems: 'center',
          }}><IconClose /></button>
        </div>
        <div style={{ padding: 18, display: 'flex', flexDirection: 'column', gap: 14 }}>
          <PyqBadge pyq={q.pyq} />
          <QuestionBody q={q} size={16} />
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {LETTERS.map((L) => (
              <OptionRow key={L} small letter={L} html={q[`option_${L.toLowerCase()}`]}
                imageUrl={q[`option_${L.toLowerCase()}_image_url`]}
                state={revealedState(L, q.your_answer, q.correct_option)} />
            ))}
          </div>
          {q.status === 'unattempted' && (
            <div style={{ fontSize: 14, color: GREY }}>This question was not answered.</div>
          )}
          <AnswerPanel correctOption={q.correct_option} explanation={q.explanation}
            imageUrl={q.explanation_image_url} refs={q.notes_refs} size={14} hrefFor={notesRefHref} />
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
            <button type="button" onClick={onPrev} disabled={!onPrev} data-testid="v2-day-popup-prev"
              style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 8, opacity: onPrev ? 1 : 0.4 }}>
              <IconArrowLeft />Previous question
            </button>
            <button type="button" onClick={onNext} disabled={!onNext} data-testid="v2-day-popup-next"
              style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 8, opacity: onNext ? 1 : 0.4 }}>
              Next question<IconArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ page

export default function AppDay() {
  const { day } = useParams();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  useActivityArea('home');
  const valid = !!parseDay(day);
  const today = istTodayIso();
  const filter = FROM_URL[params.get('show')] || 'all';
  const [openKey, setOpenKey] = useState(null);

  const dq = useQuery({
    queryKey: ['v2', 'day', day],
    queryFn: () => getDayQuestions(day),
    enabled: valid && day <= today,
  });

  useEffect(() => { setOpenKey(null); }, [day]);

  const all = useMemo(() => (dq.data && dq.data.questions) || [], [dq.data]);
  const shown = useMemo(() => (filter === 'all' ? all : all.filter((q) => q.status === filter)), [all, filter]);
  const openIdx = openKey === null ? -1 : shown.findIndex((q) => q.key === openKey);
  const close = useCallback(() => setOpenKey(null), []);
  const prev = useCallback(() => { if (openIdx > 0) setOpenKey(shown[openIdx - 1].key); }, [openIdx, shown]);
  const next = useCallback(() => { if (openIdx >= 0 && openIdx < shown.length - 1) setOpenKey(shown[openIdx + 1].key); }, [openIdx, shown]);

  const setFilter = (key) => {
    const p = new URLSearchParams(params);
    if (URL_OF[key]) p.set('show', URL_OF[key]); else p.delete('show');
    setParams(p, { replace: true });
  };
  const goDay = (iso) => navigate(`/app/day/${iso}${params.get('show') ? `?show=${params.get('show')}` : ''}`);

  const backHome = (
    <Link to="/app" style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none', boxSizing: 'border-box' }}>
      <IconArrowLeft />Back to Home
    </Link>
  );

  if (!valid || day > today) {
    return (
      <div style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {backHome}
        <div style={{ fontFamily: FONT.heading, fontSize: 22, fontWeight: 600 }}>That day was not found.</div>
      </div>
    );
  }

  const d = dq.data;
  const counts = {
    all: all.length,
    correct: d ? d.correct : 0,
    incorrect: d ? d.incorrect : 0,
    unattempted: d ? d.unattempted : 0,
  };

  return (
    <div data-testid="v2-day" style={{ maxWidth: 900, display: 'flex', flexDirection: 'column', gap: 18 }}>
      <style>{`${POINT_CSS}.v2-dayrow:hover{border-color:${C.primary}!important;box-shadow:0 1px 6px rgba(79,70,229,0.15)}.v2-dayrow:focus-visible{outline:2px solid ${C.primary};outline-offset:2px}`}</style>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        {backHome}
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <button type="button" data-testid="v2-day-prev" onClick={() => goDay(shiftDay(day, -1))}
            style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <IconArrowLeft />Previous day
          </button>
          <button type="button" data-testid="v2-day-next" disabled={day >= today} onClick={() => goDay(shiftDay(day, 1))}
            style={{ ...button.secondary, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: day >= today ? 0.4 : 1 }}>
            Next day<IconArrowRight />
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 26, fontWeight: 600 }}>
          Questions on {day === today ? 'today, ' : ''}{longDay(day)}
        </h1>
        {d && (
          <div data-testid="v2-day-summary" style={{ fontSize: 15, color: C.muted }}>
            {d.answered
              ? `${d.answered} answered · ${d.correct} correct · ${d.incorrect} wrong · ${d.accuracy_pct}% correct`
              : 'No questions answered on this day'}
            {d.unattempted ? ` · ${d.unattempted} not answered` : ''}
          </div>
        )}
      </div>

      {dq.isLoading && <Loading text="Loading the questions…" />}
      {dq.isError && <LoadError onRetry={dq.refetch} />}

      {d && all.length > 0 && (
        <>
          <div role="tablist" aria-label="Show" style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
            {FILTERS.map((f) => {
              const on = filter === f.key;
              const col = f.key === 'all' ? C.primary : STATUS[f.key].color;
              return (
                <button key={f.key} type="button" role="tab" aria-selected={on} data-testid={`v2-day-filter-${f.key}`}
                  onClick={() => setFilter(f.key)} style={{
                    minHeight: 40, padding: '0 14px', borderRadius: 999, cursor: 'pointer', fontFamily: FONT.ui,
                    fontSize: 14, fontWeight: 600, display: 'inline-flex', alignItems: 'center', gap: 8,
                    border: `1px solid ${on ? col : C.border}`, background: on ? col : C.surface,
                    color: on ? '#FFFFFF' : C.text,
                  }}>
                  {f.key !== 'all' && <span style={{ width: 10, height: 10, borderRadius: 5, background: on ? '#FFFFFF' : col }} />}
                  {f.label} ({counts[f.key]})
                </button>
              );
            })}
          </div>

          <div data-testid="v2-day-list" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {shown.length
              ? shown.map((q) => <Row key={q.key} q={q} onOpen={() => setOpenKey(q.key)} />)
              : <div style={{ fontSize: 14, color: C.muted }}>No questions here.</div>}
          </div>
        </>
      )}

      {d && all.length === 0 && (
        <div style={{ fontSize: 15, color: C.muted }}>There are no questions on this day.</div>
      )}

      {openIdx >= 0 && (
        <QuestionPopup q={shown[openIdx]} index={openIdx} count={shown.length} onClose={close}
          onPrev={openIdx > 0 ? prev : null} onNext={openIdx < shown.length - 1 ? next : null} />
      )}
    </div>
  );
}
